import { UsersApi } from '@api';
import { Card, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import {
  FormController,
  FormPasswordInput,
  formSchemas,
} from '@components/form';
import { UsersType } from '@types';
import { OverlayDialog, SwitchOnOff, toasterApi } from '@ui';
import React, { useState } from 'react';
import { z } from 'zod';

interface Props {
  user: UsersType.UserPartial;
}

const ResetPassword: React.FC<Props> = ({ user }) => {
  const [resetUserPassword] = UsersApi.Api.useResetUserPasswordMutation();
  const [enableEdit, setEnableEdit] = useState(false);

  const onFormSubmit = async (formData: z.infer<typeof resetPasswordSchema>) => {
    OverlayDialog.open('confirmUserPasswordReset', {
      overlayId: 'confirmUserPasswordReset',
      title: 'Reset Password',
      type: 'warning',
      description: `Reset password for ${user.display_name}?`,
      content: (
        <VStack align="flex-start" gap={2}>
          <Text>
            Their existing password will immediately stop working and active
            sessions may be signed out.
          </Text>
          <Text>Username: {user.username}</Text>
          <Text>Email: {user.email}</Text>
        </VStack>
      ),
      handleConfirm: async () => {
        try {
          await resetUserPassword({
            userId: user.id!,
            new_password: formData.new_password,
          }).unwrap();

          toasterApi({
            type: 'success',
            message: `Password reset for ${user.display_name}`,
          });
          setEnableEdit(false);
        } catch (err: any) {
          toasterApi({
            type: 'error',
            message:
              err?.data?.detail ||
              err?.data?.new_password?.[0] ||
              err?.data?.message ||
              'Failed to reset password',
          });
        }
      },
    });
  };

  return (
    <Stack>
      <HStack alignSelf="flex-end" gap={8}>
        <Text textStyle="heading.card" color="tertiary.fg">
          Reset Password
        </Text>
        <SwitchOnOff value={enableEdit} onValueChange={setEnableEdit} />
      </HStack>
      <Card.Root width="100%" variant="elevated">
        <Card.Body gap="7">
          <FormController
            key={`form-reset-password-${user.id}-${enableEdit}`}
            label="Reset Password"
            formSchema={resetPasswordSchema}
            helperText={
              !enableEdit
                ? 'Please Enable Edit Mode'
                : 'Enter and confirm a new password for this user.'
            }
            onFormSubmit={onFormSubmit}
            disabled={!enableEdit}
            defaultValues={{
              new_password: '',
              confirm_new_password: '',
            }}
            ButtonLabel="Reset Password"
            buttonProps={{
              variant: 'outline',
              color: 'tertiary.fg',
              width: '100%',
              mt: '4',
            }}
          >
            {({ register, control, errors }) => (
              <>
                <FormPasswordInput
                  control={control}
                  disabled={!enableEdit}
                  label="New Password"
                  id="new_password"
                  name="new_password"
                  autoComplete="new-password"
                  register={register}
                  errors={errors}
                  helperText={formSchemas.userSchema.passwordHelper}
                  required
                />
                <FormPasswordInput
                  control={control}
                  disabled={!enableEdit}
                  label="Confirm New Password"
                  id="confirm_new_password"
                  name="confirm_new_password"
                  autoComplete="new-password"
                  register={register}
                  errors={errors}
                  helperText={formSchemas.userSchema.confirmPasswordHelper}
                  required
                />
              </>
            )}
          </FormController>
        </Card.Body>
      </Card.Root>
    </Stack>
  );
};

export default ResetPassword;

const resetPasswordSchema = z
  .object({
    new_password: formSchemas.userSchema.passwordSchema,
    confirm_new_password: formSchemas.userSchema.passwordSchema,
  })
  .refine((data) => data.new_password === data.confirm_new_password, {
    message: 'Passwords do not match',
    path: ['confirm_new_password'],
  });
